import React from "react";
import { Link } from "react-router-dom";
import { createPageStyles } from "./MarketingPageStyles";
import { detectPageLocale } from "./pageLocale";

const styles = createPageStyles("dark", "stirred");

type Locale = "ko" | "en" | "ja";

const LOCALE_STORAGE_KEY = "stirred-terms-locale";
const locales: Locale[] = ["ko", "en", "ja"];
const localeLabels: Record<Locale, string> = { ko: "KO", en: "EN", ja: "JA" };

const content: Record<
  Locale,
  {
    crumb: string;
    title: string;
    accentTitle: string;
    subtitle: string;
    sections: Array<{ title: string; body?: string; list?: string[] }>;
  }
> = {
  ko: {
    crumb: "Stirred Terms",
    title: "Stirred,",
    accentTitle: "이용약관",
    subtitle:
      "최종 업데이트일: 2026년 9월 1일. 본 서비스의 운영 주체는 Daydream Factory입니다. Stirred 앱과 관련 웹페이지를 이용하는 경우 아래 약관이 적용됩니다.",
    sections: [
      {
        title: "1. 서비스 소개",
        body:
          "Stirred는 별도의 회원가입 없이 사용할 수 있는 칵테일 레시피 학습 앱입니다. 진행 기록, 즐겨찾기, 내 바 재료, 직접 만든 레시피는 모두 사용자 기기 안에 저장되며, 주요 레시피는 인터넷 연결 없이도 이용할 수 있습니다.",
      },
      {
        title: "2. 광고와 광고 제거 구매",
        list: [
          "Stirred는 무료로 사용할 수 있으며, 앱 내에 광고(Google AdMob)가 표시될 수 있습니다.",
          "광고 제거는 구독이 아닌 일회성 구매로 제공되며, 가격은 결제 화면 및 스토어에 표시된 내용을 따릅니다.",
          "구매 내역은 같은 스토어 계정에서 '구매 복원' 기능으로 복원할 수 있습니다.",
          "결제·환불은 Apple App Store 및 Google Play 정책에 따릅니다.",
        ],
      },
      {
        title: "3. 콘텐츠와 책임 있는 이용",
        list: [
          "앱에 수록된 레시피와 도수 등 수치는 일반적인 기준에 따른 참고용 정보이며, 실제 제조 결과와 다를 수 있습니다.",
          "Stirred는 음주 문화와 칵테일 제조법을 학습하기 위한 앱입니다. 지나친 음주는 건강에 해롭습니다.",
          "주류 구매·음주는 이용자가 거주하는 지역의 법정 연령과 관련 법규를 준수해야 하며, 그 책임은 이용자에게 있습니다.",
        ],
      },
      {
        title: "4. 데이터와 사용자 책임",
        list: [
          "진행 기록·즐겨찾기·내 바·나만의 레시피는 기기 로컬에 저장되며, Daydream Factory는 해당 내용에 접근하지 않습니다.",
          "기기 교체·앱 삭제 시 데이터가 사라질 수 있으므로, 중요한 내용은 사용자가 별도로 관리할 책임이 있습니다.",
        ],
      },
      {
        title: "5. 면책",
        list: [
          "Stirred는 '있는 그대로' 제공되며, 광고·결제 등 외부 서비스 상태에 따라 일부 기능이 지연되거나 제한될 수 있습니다.",
          "본 서비스 이용 또는 데이터 유실로 발생한 손해에 대해 관련 법령이 허용하는 범위 내에서 책임이 제한될 수 있습니다.",
        ],
      },
      {
        title: "6. 지적재산권",
        body:
          "Stirred 앱과 디자인, 로고, 콘텐츠에 대한 권리는 Daydream Factory에 있으며, 무단 복제·배포·역설계를 금지합니다.",
      },
      {
        title: "7. 약관 변경",
        body:
          "본 약관은 서비스 개선 또는 법령 변경에 따라 수정될 수 있으며, 중요한 변경은 앱 또는 본 페이지를 통해 안내합니다.",
      },
      {
        title: "8. 문의",
        body: "본 약관에 관한 문의: support@daydreamfactory.net",
      },
    ],
  },
  en: {
    crumb: "Stirred Terms",
    title: "Stirred,",
    accentTitle: "Terms of Service",
    subtitle:
      "Last updated: September 1, 2026. This service is operated by Daydream Factory. The following terms apply when you use the Stirred app and related web pages.",
    sections: [
      {
        title: "1. About the Service",
        body:
          "Stirred is a cocktail recipe learning app you can use without creating an account. Your progress, favorites, bar ingredients, and custom recipes are all stored on your device, and core recipes are available without an internet connection.",
      },
      {
        title: "2. Ads and the Ad-Removal Purchase",
        list: [
          "Stirred is free to use, and ads (Google AdMob) may be shown in the app.",
          "Ad removal is offered as a one-time purchase, not a subscription. The price follows what is shown on the purchase screen and in the store.",
          "Purchases can be restored on the same store account using the 'Restore purchase' feature.",
          "Billing and refunds follow Apple App Store and Google Play policies.",
        ],
      },
      {
        title: "3. Content and Responsible Use",
        list: [
          "Recipes and figures such as ABV are reference information based on common standards and may differ from actual results.",
          "Stirred is an app for learning cocktail culture and technique. Excessive drinking is harmful to your health.",
          "Purchasing and consuming alcohol must comply with the legal drinking age and laws of your region; this is the user's responsibility.",
        ],
      },
      {
        title: "4. Data and User Responsibility",
        list: [
          "Progress, favorites, your bar, and custom recipes are stored locally on your device; Daydream Factory does not access their contents.",
          "Data may be lost when changing devices or deleting the app. You are responsible for managing important content.",
        ],
      },
      {
        title: "5. Disclaimer",
        list: [
          "Stirred is provided 'as is'. Some features may be delayed or limited depending on external services such as ads and billing.",
          "To the extent permitted by applicable law, liability for damages arising from use of the service or data loss may be limited.",
        ],
      },
      {
        title: "6. Intellectual Property",
        body:
          "Rights to the Stirred app, design, logo, and content belong to Daydream Factory. Unauthorized copying, distribution, or reverse engineering is prohibited.",
      },
      {
        title: "7. Changes to Terms",
        body:
          "These terms may be updated to improve the service or to comply with law. Significant changes will be announced via the app or this page.",
      },
      {
        title: "8. Contact",
        body: "Questions about these terms: support@daydreamfactory.net",
      },
    ],
  },
  ja: {
    crumb: "Stirred Terms",
    title: "Stirred,",
    accentTitle: "利用規約",
    subtitle:
      "最終更新日: 2026年9月1日。本サービスの運営主体は Daydream Factory です。Stirred アプリおよび関連ウェブページを利用する場合、以下の規約が適用されます。",
    sections: [
      {
        title: "1. サービスについて",
        body:
          "Stirred は会員登録なしで使えるカクテルレシピ学習アプリです。進捗記録・お気に入り・マイバーの材料・オリジナルレシピはすべて端末内に保存され、主要なレシピはインターネット接続なしでも利用できます。",
      },
      {
        title: "2. 広告と広告削除の購入",
        list: [
          "Stirred は無料で利用でき、アプリ内に広告（Google AdMob）が表示される場合があります。",
          "広告削除はサブスクリプションではなく買い切りで提供され、価格は購入画面およびストアに表示される内容に従います。",
          "購入は同じストアアカウントで「購入の復元」機能により復元できます。",
          "決済・返金は Apple App Store および Google Play のポリシーに従います。",
        ],
      },
      {
        title: "3. コンテンツと責任ある利用",
        list: [
          "アプリに収録されたレシピや度数などの数値は一般的な基準に基づく参考情報であり、実際の結果と異なる場合があります。",
          "Stirred はカクテル文化と製法を学ぶためのアプリです。過度な飲酒は健康を害します。",
          "酒類の購入・飲酒は、お住まいの地域の法定年齢と関連法規に従う必要があり、その責任はユーザーにあります。",
        ],
      },
      {
        title: "4. データとユーザーの責任",
        list: [
          "進捗・お気に入り・マイバー・オリジナルレシピは端末内に保存され、Daydream Factory はその内容にアクセスしません。",
          "端末の変更・アプリの削除でデータが失われる場合があるため、重要な内容はユーザーが管理する責任があります。",
        ],
      },
      {
        title: "5. 免責",
        list: [
          "Stirred は「現状のまま」提供され、広告・決済など外部サービスの状態により一部機能が遅延・制限される場合があります。",
          "本サービスの利用またはデータ消失により生じた損害については、適用法令が認める範囲で責任が制限される場合があります。",
        ],
      },
      {
        title: "6. 知的財産権",
        body:
          "Stirred アプリ、デザイン、ロゴ、コンテンツに関する権利は Daydream Factory に帰属し、無断複製・配布・リバースエンジニアリングを禁止します。",
      },
      {
        title: "7. 規約の変更",
        body:
          "本規約はサービス改善または法令の変更に応じて改定される場合があり、重要な変更はアプリまたは本ページでお知らせします。",
      },
      {
        title: "8. お問い合わせ",
        body: "本規約に関するお問い合わせ: support@daydreamfactory.net",
      },
    ],
  },
};

const StirredTerms: React.FC = () => {
  const [locale, setLocale] = React.useState<Locale>("en");

  React.useEffect(() => {
    setLocale(detectPageLocale(LOCALE_STORAGE_KEY, locales, "en"));
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const copy = content[locale];

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.topRow}>
          <Link to="/" style={styles.homeLink}>
            ← Daydream Factory
          </Link>
          <div style={styles.topRowMeta}>
            <div style={styles.localeSwitch} aria-label="Language switcher">
              {locales.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setLocale(item)}
                  style={{
                    ...styles.localeButton,
                    ...(locale === item ? styles.activeLocaleButton : {}),
                  }}
                >
                  {localeLabels[item]}
                </button>
              ))}
            </div>
            <span style={styles.crumb}>{copy.crumb}</span>
          </div>
        </div>

        <section style={styles.hero}>
          <h1 style={styles.title}>
            {copy.title}
            <br />
            <span style={styles.accentTitle}>{copy.accentTitle}</span>
          </h1>
          <p style={styles.subtitle}>{copy.subtitle}</p>
        </section>

        {copy.sections.map((section) => (
          <section key={section.title} style={styles.section}>
            <h2 style={styles.h2}>{section.title}</h2>
            <div style={styles.sectionCard}>
              {section.body ? (
                <p style={styles.body}>
                  {section.body.includes("support@daydreamfactory.net") ? (
                    <>
                      {section.body.replace("support@daydreamfactory.net", "")}
                      <a href="mailto:support@daydreamfactory.net" style={styles.link}>
                        support@daydreamfactory.net
                      </a>
                    </>
                  ) : (
                    section.body
                  )}
                </p>
              ) : null}
              {section.list ? (
                <ul style={styles.list}>
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
};

export default StirredTerms;
